'use client';

import { useTranslations } from 'next-intl';
import React, { useState } from 'react';

import { Input } from '@/vibes/mom/form/input';
import { Button } from '@/vibes/mom/primitives/button';
import { toast } from '@/vibes/mom/primitives/toaster';
import { Link } from '~/components/link';
import { env } from '~/lib/atom/env';
import { setCookie } from '~/lib/client-cookies';

export function EmailSignupForm() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  const t = useTranslations('Components.EmailSubscribe');

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const companyId = env.NEXT_PUBLIC_KLAVIYO_COMPANY_ID;
      const listId = env.NEXT_PUBLIC_KLAVIYO_LIST_ID;

      if (!companyId || !listId) {
        throw new Error('Klaviyo configuration is missing');
      }

      const url = `https://a.klaviyo.com/client/subscriptions?company_id=${companyId}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          revision: '2024-10-15',
          'content-type': 'application/vnd.api+json',
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
              custom_source: 'Subscriptions',
            },
            relationships: {
              list: {
                data: { type: 'list', id: listId },
              },
            },
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(errorText || 'Failed to subscribe');
      }

      setCookie('klaviyo_email_signup', 'true', { path: '/', sameSite: 'Lax' });

      setEmail('');
      toast.success(t('successMessage'));

      setTimeout(() => {
        const formContainer = document.querySelector('.klaviyo-email-signup');

        if (formContainer) {
          formContainer.classList.add('hidden');
          setTimeout(() => {
            setIsHidden(true);
          }, 500);
        } else {
          setIsHidden(true);
        }
      }, 3000);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error subscribing to newsletter:', error);
      toast.error(t('errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isHidden) {
    return null;
  }

  return (
    <div className="w-full max-w-[560px]">
      <form className="flex w-full flex-col items-center gap-4 md:flex-row" onSubmit={handleSubmit}>
        <Input
          className="w-full md:min-w-[400px]"
          disabled={isSubmitting}
          name="email"
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('placeholder')}
          required
          type="email"
          value={email}
        />
        <Button
          className="w-full md:min-w-[160px]"
          disabled={isSubmitting}
          loading={isSubmitting}
          shape="rounded"
          size="medium"
          type="submit"
          variant="primary"
        >
          {t('submitText')}
        </Button>
      </form>

      <p className="mt-2 text-center text-sm text-[#423D3D]">
        {t.rich('privacyPolicyCopy', {
          privacyLink: (chunks) => (
            <Link
              className="text-mom-brandColor underline hover:no-underline"
              href="/privacy-policy/"
            >
              {chunks}
            </Link>
          ),
        })}
      </p>
    </div>
  );
}
